// User settings persisted to localStorage. `muted`/`volume` drive the background
// music engine (see audio/music-controller); reduced-motion toggles a root class
// the CSS uses to cut transitions. Subscribers are notified on every apply so the
// audio engine can react to volume/mute changes live.

export interface Settings {
  muted: boolean;
  volume: number; // 0..100
  reducedMotion: boolean;
  // Enter browser fullscreen when a match starts (it then lasts for the visit;
  // see shared/fullscreen.ts). Read at match start in App.tsx.
  autoFullscreen: boolean;
  // A short vibration on every button press (the Android app only; shared/ui-feedback.ts).
  vibration: boolean;
  // How strong it is (shared/ui-feedback.ts HAPTICS).
  vibrationStrength: VibrationStrength;
}
export type VibrationStrength = "light" | "normal" | "strong";

const KEY = "ar.settings.v1";
const DEFAULTS: Settings = { muted: false, volume: 70, reducedMotion: false, autoFullscreen: true, vibration: true, vibrationStrength: "normal" };

type Listener = (s: Settings) => void;
const listeners = new Set<Listener>();

export function getSettings(): Settings {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

export function setSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
  applySettings(s);
}

// Subscribe to settings changes. Returns an unsubscribe function.
export function subscribeSettings(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function applySettings(s: Settings): void {
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("reduced-motion", !!s.reducedMotion);
  }
  listeners.forEach((fn) => {
    try {
      fn(s);
    } catch {
      /* a misbehaving listener must not break the rest */
    }
  });
}
